import { useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Questionnaire } from '@shadcn/react/questionnaire'
import { ChevronLeft, ChevronRight, HelpCircle, X } from 'lucide-react'
import type {
  ClarifyingAnswerMap,
  ClarifyingQuestion,
  ToolCallDisplay,
} from '@/types'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card'
import { useConversationContext } from '@/contexts/ConversationContext'
import { useMountEffect } from '@/hooks/useMountEffect'
import { cn } from '@/lib/utils'
import { AnsweredView } from './AnsweredView'
import { QuestionView } from './QuestionView'
import {
  draftsToAnswers,
  emptyDraft,
  isResolved,
  parseAnswerResult,
  parseQuestions,
  type QuestionDraft,
} from './utils'

// Matches hoku/tools/clarify.py CLARIFYING_TOOL_NAME — the tool whose calls
// render as this card.
export const CLARIFYING_TOOL_NAME = 'ask_clarifying_questions'

// Brief pause after a definitive choice so the selection registers visually
// before the card slides to the next question (Cowork-style).
const AUTO_ADVANCE_MS = 220

// `inline` renders inside the transcript and only shows the resolved/answered
// view (the active question floats above the composer instead). `floating`
// renders the interactive card above the composer while a question is pending.
type Placement = 'inline' | 'floating'

interface ClarifyingQuestionMessageProps {
  event: ToolCallDisplay
  placement?: Placement
}

/** True once the deferred call has any result — submitted here or serviced
 * elsewhere (e.g. the user replied in the main chat). */
export function isClarifyingResolved(
  event: ToolCallDisplay,
  clarifyingAnswers: Record<string, unknown>
): boolean {
  const local = event.toolCallId
    ? clarifyingAnswers[event.toolCallId]
    : undefined
  return local != null || event.result != null
}

export function ClarifyingQuestionMessage({
  event,
  placement = 'inline',
}: ClarifyingQuestionMessageProps) {
  const { submitClarifyingAnswer, clarifyingAnswers, streamingState } =
    useConversationContext()
  const [searchParams] = useSearchParams()
  const conversationId = searchParams.get('conversationId') || undefined
  const toolCallId = event.toolCallId

  const questions = useMemo(() => parseQuestions(event.args), [event.args])
  const questionnaireItems = useMemo(
    () =>
      questions.map((question) => ({
        name: question.id,
        required: question.required === true,
        choices: question.options.map((option) => ({ value: option.label })),
      })),
    [questions]
  )

  const localPayload = toolCallId ? clarifyingAnswers[toolCallId] : undefined
  const answeredPayload = localPayload ?? parseAnswerResult(event.result)
  const resolved = isClarifyingResolved(event, clarifyingAnswers)

  const [drafts, setDrafts] = useState<Record<string, QuestionDraft>>({})
  const [page, setPage] = useState(0)

  const advanceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const cancelAdvance = () => {
    if (advanceTimerRef.current) clearTimeout(advanceTimerRef.current)
  }
  useMountEffect(() => cancelAdvance)

  // Skip on the last question makes the questionnaire submit the whole form on
  // the next microtask. Swallow that one; the user submits with Submit.
  const skipSubmitRef = useRef(false)
  const handleSkip = () => {
    skipSubmitRef.current = true
    setTimeout(() => {
      skipSubmitRef.current = false
    })
  }

  // Inline (transcript): only the resolved/answered view lives here; while a
  // question is still pending it is shown by the floating instance instead.
  if (placement === 'inline') {
    if (!resolved) return null
    return (
      <CardShell>
        <CardContent className="pt-0">
          {answeredPayload ? (
            <AnsweredView questions={questions} payload={answeredPayload} />
          ) : (
            <p className="text-sm text-muted-foreground">
              You continued in the chat, so these questions were skipped.
            </p>
          )}
        </CardContent>
      </CardShell>
    )
  }

  // Floating (above composer): only the live, unresolved card renders here.
  if (resolved) return null

  if (questions.length === 0) {
    return (
      <CardShell floating>
        <CardContent className="py-3 text-sm text-muted-foreground">
          Preparing a few questions…
        </CardContent>
      </CardShell>
    )
  }

  const currentPage = Math.min(page, questions.length - 1)
  const current = questions[currentPage]
  if (!current) return null
  const lastPage = questions.length - 1
  const draftFor = (q: ClarifyingQuestion) => drafts[q.id] ?? emptyDraft()
  const setDraft = (id: string, next: QuestionDraft) =>
    setDrafts((prev) => ({ ...prev, [id]: next }))

  // Every question needs an answer or an explicit Skip. A required one has no
  // Skip, so this also keeps the card unsubmittable until it is answered.
  const allResolved = questions.every((q) => isResolved(draftFor(q)))
  // A required question holds the user on it until it has an answer.
  const blocksNext = current.required === true && !isResolved(draftFor(current))
  const busy = streamingState.isStreaming || !conversationId

  const submit = (answers: ClarifyingAnswerMap) => {
    if (!conversationId || !toolCallId) return
    submitClarifyingAnswer(toolCallId, { answers }, conversationId)
  }

  // Advance to the next question after a definitive choice. No-op on the last
  // question (the user submits from there).
  const advance = () => {
    cancelAdvance()
    advanceTimerRef.current = setTimeout(() => {
      advanceTimerRef.current = null
      setPage((p) => Math.min(lastPage, p + 1))
    }, AUTO_ADVANCE_MS)
  }

  const handleSubmit = () => submit(draftsToAnswers(questions, drafts))
  const handleDismiss = () => {
    const skippedAll: ClarifyingAnswerMap = {}
    for (const q of questions)
      skippedAll[q.id] = { selected: [], skipped: true }
    submit(skippedAll)
  }

  return (
    <Questionnaire.Root
      aria-label="Clarifying questions"
      className="w-full max-w-xl"
      item={current.id}
      items={questionnaireItems}
      shortcuts="numbers"
      onItemChange={(item) => {
        cancelAdvance()
        const nextPage = questions.findIndex((question) => question.id === item)
        if (nextPage >= 0) setPage(nextPage)
      }}
      onKeyDown={(keyboardEvent) => {
        if (
          busy &&
          (keyboardEvent.key === 'Enter' ||
            keyboardEvent.key.startsWith('Arrow') ||
            /^[1-9]$/.test(keyboardEvent.key))
        ) {
          keyboardEvent.preventDefault()
        }
      }}
      onSubmit={(submitEvent) => {
        submitEvent.preventDefault()
        if (
          skipSubmitRef.current &&
          !(submitEvent.nativeEvent.submitter instanceof HTMLButtonElement)
        )
          return
        if (!busy && allResolved) handleSubmit()
      }}>
      <CardShell floating>
        <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-3">
          <span className="inline-flex items-center gap-1.5 rounded-md bg-david-solid/10 px-2 py-1 text-xs font-medium text-david-solid">
            <HelpCircle className="size-3.5" />
            {current.header || 'Question'}
          </span>
          <div className="flex items-center gap-1">
            {questions.length > 1 && (
              <>
                <Questionnaire.Previous
                  aria-hidden={false}
                  hidden={false}
                  inert={false}
                  disabled={busy || currentPage === 0}
                  aria-label="Previous question"
                  render={
                    <Button variant="ghost" size="icon" className="size-7">
                      <ChevronLeft className="size-4" />
                    </Button>
                  }
                />
                <Questionnaire.Progress className="text-xs tabular-nums text-muted-foreground">
                  {currentPage + 1} of {questions.length}
                </Questionnaire.Progress>
                <Questionnaire.Next
                  aria-hidden={false}
                  hidden={false}
                  inert={false}
                  disabled={busy || currentPage >= lastPage || blocksNext}
                  aria-label="Next question"
                  render={
                    <Button variant="ghost" size="icon" className="size-7">
                      <ChevronRight className="size-4" />
                    </Button>
                  }
                />
              </>
            )}
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-7"
              disabled={busy}
              onClick={handleDismiss}
              aria-label="Dismiss and skip all">
              <X className="size-4" />
            </Button>
          </div>
        </CardHeader>

        <CardContent className="pb-3">
          {questions.map((question, index) => (
            <QuestionView
              key={question.id}
              question={question}
              draft={draftFor(question)}
              disabled={busy}
              onChange={(next) => setDraft(question.id, next)}
              onAdvance={advance}
              onSkip={handleSkip}
              hasNextQuestion={index < lastPage}
            />
          ))}
        </CardContent>

        <CardFooter className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 border-t pt-3">
          <span className="mr-auto text-xs text-muted-foreground">
            ⌘/Ctrl + Enter to {currentPage < lastPage ? 'continue' : 'submit'}
          </span>
          <Questionnaire.Next
            disabled={busy || blocksNext}
            render={primaryButton}>
            Next
          </Questionnaire.Next>
          <Questionnaire.Submit
            disabled={busy || !allResolved}
            render={primaryButton}>
            Submit
          </Questionnaire.Submit>
        </CardFooter>
      </CardShell>
    </Questionnaire.Root>
  )
}

const primaryButton = (
  <Button
    size="sm"
    className="bg-david-solid text-white hover:bg-david-hover-solid"
  />
)

function CardShell({
  children,
  floating = false,
}: {
  children: React.ReactNode
  floating?: boolean
}) {
  return (
    <Card
      className={cn(
        // Frosted frame matching the chat table's outer (base) layer:
        // white-alpha-4 tint, border, 16px radius.
        'w-full max-w-xl gap-0 rounded-2xl border border-border bg-white-alpha-4 py-3',
        floating && 'shadow-card'
      )}
      role="group"
      aria-label="Clarifying questions">
      {children}
    </Card>
  )
}
